import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useContext,
  useEffect,
} from 'react'
import { TextInput } from 'react-native'
import { useField } from 'formik'
import { styled, YStack, XStack, isWeb } from 'tamagui'

import { Text } from '../Text'
import { Icon } from '../Icon'
import { FormFieldContext } from './shared'

import './FormInput.css'
import {
  searchCities,
  getTimezoneDisplayLabel,
  CityEntry,
} from '../../data/cityTimezones'

const TriggerButton = styled(XStack, {
  className: 'FormInput',
  borderWidth: 1,
  borderRadius: '$2',
  borderColor: '$borderColor',
  paddingHorizontal: '$3',
  paddingVertical: '$2',
  minHeight: 38,
  alignItems: 'center',
  gap: '$2',
  cursor: 'pointer',
  backgroundColor: '$background',

  hoverStyle: {
    borderColor: '$borderColorHover',
  },

  focusStyle: {
    borderColor: '$borderColorFocus',
  },
})

const SearchInput = styled(
  TextInput,
  {
    name: 'TimezoneSearchInput',
    borderWidth: 1,
    borderRadius: '$2',
    borderColor: '$borderColor',
    placeholderTextColor: '$colorMuted',
    padding: 8,
    color: '$color',

    hoverStyle: {
      borderColor: '$borderColorHover',
    },

    focusStyle: {
      borderColor: '$borderColorFocus',
    },
  } as const,
  { isInput: true }
)

const DropdownContainer = styled(YStack, {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1000,
  backgroundColor: '$background',
  borderWidth: 1,
  borderColor: '$borderColor',
  borderRadius: '$2',
  marginTop: '$1',
  overflow: 'hidden',
  ...(isWeb
    ? {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      }
    : {
        elevation: 4,
      }),
})

const ResultItem = styled(XStack, {
  paddingHorizontal: '$3',
  paddingVertical: '$3',
  alignItems: 'center',
  cursor: 'pointer',
  borderBottomWidth: 1,
  borderBottomColor: '$borderColor',

  hoverStyle: {
    backgroundColor: '$backgroundStrong',
  },

  variants: {
    highlighted: {
      true: {
        backgroundColor: '$backgroundStrong',
      },
    },
  } as const,
})

export const FormTimezonePicker = ({
  placeholder = 'Search for a city...',
}: {
  placeholder?: string
}) => {
  const formFieldContext = useContext(FormFieldContext)
  const [field, , helpers] = useField<string>(formFieldContext.name)
  const { onBlur: formFieldOnBlur, onFocus: formFieldOnFocus } =
    formFieldContext

  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const containerRef = useRef<React.ElementRef<typeof YStack>>(null)
  const inputRef = useRef<TextInput>(null)

  const results = useMemo(() => {
    if (!searchQuery || searchQuery.length < 1) return []
    return searchCities(searchQuery)
  }, [searchQuery])

  const displayLabel = useMemo(() => {
    if (!field.value) return null
    return getTimezoneDisplayLabel(field.value)
  }, [field.value])

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    setSearchQuery('')
    setHighlightedIndex(-1)
    formFieldOnFocus?.()
    // Focus the input after opening
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }, [formFieldOnFocus])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setSearchQuery('')
    setHighlightedIndex(-1)
    formFieldOnBlur?.()
  }, [formFieldOnBlur])

  const handleSelect = useCallback(
    (entry: CityEntry) => {
      void helpers.setValue(entry.timezone)
      void helpers.setTouched(true)
      handleClose()
    },
    [helpers, handleClose]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev < results.length - 1 ? prev + 1 : 0
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : results.length - 1
          )
          break
        case 'Enter':
          e.preventDefault()
          if (highlightedIndex >= 0 && highlightedIndex < results.length) {
            handleSelect(results[highlightedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          handleClose()
          break
      }
    },
    [isOpen, results, highlightedIndex, handleSelect, handleClose]
  )

  // Click outside to dismiss (web only)
  useEffect(() => {
    if (!isWeb || !isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      const container = containerRef.current
      if (
        container instanceof Element &&
        !container.contains(e.target as Node)
      ) {
        handleClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClose])

  // Reset highlighted index when results change
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [results])

  if (isOpen) {
    return (
      <YStack
        ref={containerRef}
        position='relative'
        zIndex={1000}
        {...(isWeb
          ? {
              onKeyDown: handleKeyDown,
              style: { zIndex: 1000 },
            }
          : {})}
      >
        <SearchInput
          ref={inputRef}
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder={placeholder}
          autoFocus
          {...(isWeb ? { style: { outline: 'none' } } : {})}
        />
        {results.length > 0 && (
          <DropdownContainer>
            {results.map((entry, index) => {
              const label = getTimezoneDisplayLabel(entry.timezone)
              return (
                <ResultItem
                  key={entry.timezone}
                  highlighted={index === highlightedIndex}
                  onPress={() => handleSelect(entry)}
                >
                  <YStack flex={1} gap='$1'>
                    <Text size='$4' color='$colorStrong' fontWeight='$6'>
                      {entry.city}, {entry.country}
                    </Text>
                    <Text size='$3' color='$colorMuted'>
                      {label.timezoneLabel}
                    </Text>
                  </YStack>
                </ResultItem>
              )
            })}
          </DropdownContainer>
        )}
        {searchQuery.length > 0 && results.length === 0 && (
          <DropdownContainer>
            <YStack padding='$3'>
              <Text size='$4' color='$colorMuted'>
                No cities found matching &quot;{searchQuery}&quot;
              </Text>
            </YStack>
          </DropdownContainer>
        )}
      </YStack>
    )
  }

  return (
    <TriggerButton onPress={handleOpen}>
      <Icon name='globe-clock' size='$4' color='$colorMuted' />
      {displayLabel ? (
        <YStack flex={1} gap={2}>
          <Text size='$4' color='$colorStrong' fontWeight='$5'>
            {displayLabel.cityLabel}
          </Text>
          <Text size='$3' color='$colorMuted'>
            {displayLabel.timezoneLabel}
          </Text>
        </YStack>
      ) : (
        <Text size='$4' color='$colorMuted' flex={1}>
          Select a timezone
        </Text>
      )}
      <Icon name='chevron-down' size='$3' color='$colorMuted' />
    </TriggerButton>
  )
}
